import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DynamicDataSheetGrid, createContextMenuComponent, createTextColumn, keyColumn } from 'react-datasheet-grid'
import type { CellProps, Column, ContextMenuComponentProps, ContextMenuItem, DataSheetGridRef } from 'react-datasheet-grid'
import 'react-datasheet-grid/dist/style.css'
// 그리드는 DynamicDataSheetGrid — 기본 DataSheetGrid(Static)는 처음 받은 columns·콜백을 계속 써서 행 끝 칸·점검 표시·공동 편집 표시가 안 바뀐다
import type { DeviceSpecDevice } from '../../types'
import { PAD, dropBlankRows, useLeaveBlankRow } from './blankRows'
import { CHANNELS, channelDef } from './catalog'
import ChoiceCell from './ChoiceCell'
import type { ChoiceData } from './ChoiceCell'
import type { SpecIssues } from './model'
import { changeChannelType, emptyDevice, newKey } from './model'
import { seriesValue } from './fillSeries'
import { releaseGrid, useF2Edit } from './useF2Edit'
import { useFillHandle } from './useFillHandle'
import type { Sel } from './useFillHandle'
import { ViewerChips } from './Presence'
import { cellMarks, markClass } from './presenceMarks'
import { useLocate } from './useLocate'
import type { CellNote } from './SheetStatus'
import { hasAddress } from './useSpecDraft'
import type { SpecViewer } from './useSpecDraft'

// 디바이스 시트 — 명세서 상세 화면의 본문. 디바이스 한 대 = 한 행, 엑셀처럼 방향키 이동·범위 복사/붙여넣기(엑셀과 서로)·
// Delete 로 지우기, 우클릭으로 행 삽입·복제·삭제. 표 아래 빈 행에 한 칸이라도 적으면 새 디바이스가 되고, 칸을 다 지운 행은
// 없어진다 (blankRows — 태그·메모가 있는 디바이스는 남긴다).
// 채우기 핸들(useFillHandle): 그냥 끌면 복사, Ctrl + 끌기는 자동 증가 (설비 1호기 → 2호기, IF01 → IF02). 프로토콜은 늘 복사.
// 표 아래로 끌면 디바이스가 그만큼 생긴다.
// 태그는 여기서 고치지 않는다 — 프로토콜을 고르면 행 끝에 [태그] 가 생겨 편집 모드(그 디바이스 탭)를 연다. 접속(IP·포트 등)과 채널 상세는 [설정] 서랍에서
// (전송 전 점검에서 접속이 빠졌으면 프로토콜 칸을 붉게).

const ROW_HEIGHT = 32
const PAD_ROWS = 3

interface DeviceRow {
  key: string
  from?: string // 우클릭 [행 복제] 로 만든 행 — 원본 디바이스의 채널·태그를 이어받는다
  category: string
  maker: string
  model: string
  name: string
  channelType: string
  code: string
}

const TEXT_FIELDS = ['category', 'maker', 'model', 'name', 'code'] as const
// 열 순서 = 그리드 선택 영역의 col 번호
const COLUMN_FIELDS = ['category', 'maker', 'model', 'name', 'channelType', 'code'] as const
export type DeviceGridField = typeof COLUMN_FIELDS[number]

const toRow = (d: DeviceSpecDevice): DeviceRow => ({
  key: d.key, category: d.category, maker: d.maker, model: d.model, name: d.name,
  channelType: d.channelType, code: d.code,
})
const blankRow = (key: string): DeviceRow => ({
  key, category: '', maker: '', model: '', name: '', channelType: '', code: '',
})
const isBlank = (r: DeviceRow) => !r.channelType && TEXT_FIELDS.every(f => !r[f].trim())
// 시트 칸이 비어도 태그·메모·설명이 있으면 지우지 않는다 ([설정] 서랍에만 적은 것)
const isEmptyDevice = (d: DeviceSpecDevice | undefined) => !d || (!d.tags.length && !d.memo?.trim() && !d.description?.trim())

// 붙여넣은 프로토콜 — 유형 이름·표시 이름을 대소문자·띄어쓰기 무시하고 맞추고, 없으면 이름에 들어 있는 것으로
const squash = (v: string) => v.toLowerCase().replace(/[\s/_-]+/g, '')
function parseChannelType(raw: string): string {
  const v = squash(raw)
  if (!v) return ''
  return (CHANNELS.find(c => squash(c.type) === v || squash(c.label) === v)
    ?? CHANNELS.find(c => squash(c.label).includes(v) || squash(c.type).includes(v)))?.type ?? ''
}

const MENU_LABELS: Record<ContextMenuItem['type'], string> = {
  INSERT_ROW_BELLOW: '아래에 디바이스 삽입',
  DUPLICATE_ROW: '디바이스 복제',
  DELETE_ROW: '디바이스 삭제',
  DUPLICATE_ROWS: '선택한 디바이스 복제',
  DELETE_ROWS: '선택한 디바이스 삭제',
  COPY: '복사',
  CUT: '잘라내기',
  PASTE: '붙여넣기',
}
const ContextMenuBase = createContextMenuComponent(item => (
  <>{MENU_LABELS[item.type]}{'fromRow' in item && <span className="text-gray-400 ml-1">({item.fromRow + 1}~{item.toRow + 1})</span>}</>
))
const ContextMenu = (props: ContextMenuComponentProps) => <ContextMenuBase {...props} />

const text = () => createTextColumn<string>({
  deletedValue: '', continuousUpdates: false,
  parseUserInput: v => v, parsePastedValue: v => v.trim(), formatBlurredInput: v => v ?? '',
})

const channelColumn: Partial<Column<string, ChoiceData, string>> = {
  component: ChoiceCell,
  columnData: { options: CHANNELS.map(c => ({ value: c.type, label: c.label })), placeholder: '프로토콜' },
  keepFocus: true,
  disableKeys: true,
  deleteValue: () => '',
  copyValue: ({ rowData }) => channelDef(rowData)?.label ?? rowData,
  pasteValue: ({ value }) => parseChannelType(value),
}

// 행 끝 — [설정] (채널 상세 서랍) · [태그] + 태그 수 (편집 모드). 한 칸이라도 적어 디바이스가 되면 바로 보이고,
// [태그] 는 프로토콜을 고른 뒤에 (이미 태그가 있으면 프로토콜이 없어도). 빈 행에는 없다
interface ActionData {
  devices: Map<string, DeviceSpecDevice>
  tagIssues: (d: DeviceSpecDevice) => number
  onEditTags: (key: string) => void
  onOpenPanel: (key: string) => void
  others: (key: string) => SpecViewer[] // 이 디바이스에서 작업 중인 다른 사람
}
function ActionCell({ rowData, columnData }: CellProps<DeviceRow, ActionData>) {
  const device = columnData.devices.get(rowData.key)
  if (!device) return null
  const count = device.tags.filter(hasAddress).length
  const issues = columnData.tagIssues(device)
  const stop = (e: React.MouseEvent) => e.stopPropagation() // 그리드가 칸 선택으로 가져가지 않게
  const canTag = !!channelDef(device.channelType) || device.tags.length > 0
  return (
    <div className="w-full h-full flex items-center gap-1 px-1.5 text-xs min-w-0" onMouseDown={stop}>
      <button onClick={() => columnData.onOpenPanel(device.key)} title="접속 정보, 채널 설정, 메모"
        className="px-1.5 py-0.5 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 whitespace-nowrap">설정</button>
      {canTag
        ? (
          <button onClick={() => columnData.onEditTags(device.key)} title="편집 모드에서 이 디바이스의 태그 작성 (Ctrl+Shift+E)"
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-accent hover:bg-blue-50 whitespace-nowrap">
            태그 <span className={count ? 'text-gray-600' : 'text-gray-400'}>{count}</span>
            {issues > 0 && <span className="text-red-600 font-medium" title="전송 전 점검에 걸린 태그 칸">· 고칠 칸 {issues}</span>}
          </button>
        )
        : <span className="px-1 text-gray-500 whitespace-nowrap" title="프로토콜을 고르면 태그를 추가할 수 있습니다">프로토콜을 고르면 태그</span>}
      <span className="ml-auto pl-1 min-w-0 overflow-hidden"><ViewerChips viewers={columnData.others(device.key)} /></span>
    </div>
  )
}

/** 바깥(검색·단축키)에서 시트를 다루는 손잡이 */
export interface DeviceGridHandle {
  /** 그 디바이스 행의 칸으로 커서를 옮기고 화면에 보이게 스크롤 */
  focusDevice: (key: string, field?: DeviceGridField) => void
  /** 커서가 있는 디바이스 (없으면 null) */
  activeKey: () => string | null
  /** 커서가 있는 칸 이름 (없으면 null) */
  activeField: () => DeviceGridField | null
  /** 표에서 커서를 뺀다 ([설정] 서랍 등) — 돌려놓는 함수를 준다 */
  release: () => () => void
}

export default function DeviceGrid({ devices, issues, showIssues, viewers, onChange, onEditTags, onOpenPanel, onNotice, onLocate, onNote, handleRef }: {
  devices: DeviceSpecDevice[]
  issues: SpecIssues
  showIssues: boolean
  viewers: SpecViewer[] // 나를 뺀 접속자
  onChange: (devices: DeviceSpecDevice[]) => void
  onEditTags: (key: string) => void
  onOpenPanel: (key: string) => void
  onNotice: (message: string) => void
  onLocate: (deviceKey: string | null, field: string | null, editing: boolean) => void // 내 커서 위치·입력 중 (공동 편집 표시)
  onNote?: (note: CellNote | null) => void // 지금 칸이 붉은 이유 (시트 아래 줄)
  handleRef?: (handle: DeviceGridHandle | null) => void
}) {
  const gridRef = useRef<DataSheetGridRef>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  useF2Edit(gridRef)

  const display = useMemo(
    () => [...devices.map(toRow), ...Array.from({ length: PAD_ROWS }, (_, j) => blankRow(`${PAD}${devices.length + j}`))],
    [devices],
  )
  const displayRef = useRef(display)
  const handleRefRef = useRef(handleRef)
  useEffect(() => { displayRef.current = display; handleRefRef.current = handleRef })

  useEffect(() => {
    const register = handleRefRef.current
    register?.({
      focusDevice: (key, field) => {
        const grid = gridRef.current
        const row = displayRef.current.findIndex(r => r.key === key)
        if (!grid || row < 0) return
        grid.setActiveCell({ col: Math.max(0, COLUMN_FIELDS.indexOf(field ?? 'name')), row })
        setTimeout(() => wrapRef.current?.querySelector('.dsg-active-cell')?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 60)
      },
      activeKey: () => {
        const cell = gridRef.current?.activeCell
        const key = cell ? displayRef.current[cell.row]?.key : undefined
        return key && !key.startsWith(PAD) ? key : null
      },
      activeField: () => {
        const cell = gridRef.current?.activeCell
        return cell ? COLUMN_FIELDS[cell.col] ?? null : null
      },
      release: () => releaseGrid(gridRef),
    })
    return () => register?.(null)
  }, [])

  const byKey = useMemo(() => new Map(devices.map(d => [d.key, d])), [devices])

  // 지금 칸이 붉은 이유 (전송 전 점검) — 프로토콜 칸은 접속 정보([설정] 서랍)도 같이
  const [activeCell, setActiveCell] = useState<{ col: number; row: number } | null>(null)
  const note = useMemo<CellNote | null>(() => {
    const key = activeCell ? display[activeCell.row]?.key : undefined
    const field = activeCell ? COLUMN_FIELDS[activeCell.col] : undefined
    const e = showIssues && key ? issues.device[key] : undefined
    if (!e || !field) return null
    const name = byKey.get(key!)?.name || `${activeCell!.row + 1}행`
    if (field === 'name' && e.name) return { tone: 'error', text: `${name} · 디바이스 이름 — ${e.name}` }
    if (field === 'code' && e.code) return { tone: 'error', text: `${name} · 디바이스 코드 — ${e.code === '중복' ? '다른 디바이스와 코드가 같습니다' : e.code}` }
    if (field === 'channelType' && e.channelType) return { tone: 'error', text: `${name} · 프로토콜 — ${e.channelType}` }
    if (field === 'channelType' && e.endpoint) return { tone: 'error', text: `${name} · 접속 정보 — ${e.endpoint} (행 끝 [설정] 에서 입력)` }
    return null
  }, [activeCell, display, showIssues, issues, byKey])
  const onNoteRef = useRef(onNote)
  useEffect(() => { onNoteRef.current = onNote })
  useEffect(() => { onNoteRef.current?.(note) }, [note])
  useEffect(() => () => onNoteRef.current?.(null), [])

  // ── 채우기 핸들 ──
  const locateCell = useLocate(wrapRef, onLocate)
  const fill = useFillHandle({
    wrapRef, rowHeight: ROW_HEIGHT,
    activeCell: () => gridRef.current?.activeCell ?? null,
    // Ctrl 일 때 자동 증가가 되는지 — 글자 칸 중 하나라도 이어 갈 숫자가 있으면
    increments: sel => ctrl => ctrl && COLUMN_FIELDS.slice(sel.min.col, sel.max.col + 1).some(f =>
      f !== 'channelType' && seriesValue(displayRef.current.slice(sel.min.row, sel.max.row + 1).map(r => r[f]), 1, true) != null),
    onFill: (sel, target, ctrl) => fillDown(sel, target, ctrl),
  })

  // 원본 행(sel)을 target 행까지 — 빈 행·표 아래까지 끌면 새 디바이스 (handleChange 가 만든다)
  const fillDown = (sel: Sel, target: number, ctrl: boolean) => {
    const rows = [...displayRef.current]
    while (rows.length <= target) rows.push(blankRow(`${PAD}${rows.length}`))
    for (const field of COLUMN_FIELDS.slice(sel.min.col, sel.max.col + 1)) {
      const src = rows.slice(sel.min.row, sel.max.row + 1).map(r => r[field])
      for (let row = sel.max.row + 1; row <= target; row++) {
        const k = row - sel.max.row
        const series = field === 'channelType' ? null : seriesValue(src, k, ctrl)
        rows[row] = { ...rows[row], [field]: series ?? src[(k - 1) % src.length] }
      }
    }
    handleChange(rows)
    // 채운 범위를 선택해 둔다 (늘어난 행이 그려진 뒤)
    requestAnimationFrame(() => gridRef.current?.setSelection({
      min: { col: sel.min.col, row: sel.min.row }, max: { col: sel.max.col, row: target },
    }))
  }

  // 행 → 디바이스. 기존 디바이스는 시트 칸만 덮고, 프로토콜이 바뀌면 채널을 새 유형으로 (같은 이름 필드는 이어받기)
  const handleChange = (rows: DeviceRow[]) => {
    const kept = dropBlankRows(rows, displayRef.current, r => isBlank(r) && isEmptyDevice(byKey.get(r.key)))

    const next = kept.map(row => {
      const existing = byKey.get(row.key)
      const origin = row.from ? byKey.get(row.from) : undefined
      // 새 행은 행 키를 그대로 디바이스 키로 (빈 행이면 새 키)
      const key = row.key.startsWith(PAD) ? newKey() : row.key
      const src: DeviceSpecDevice = existing
        ?? (origin ? { ...origin, key, channel: { ...origin.channel }, tags: origin.tags.map(t => ({ ...t, key: newKey() })) } : { ...emptyDevice(), key })
      let d: DeviceSpecDevice = {
        ...src, category: row.category, maker: row.maker, model: row.model, name: row.name,
        code: row.code,
      }
      if (row.channelType !== src.channelType) d = changeChannelType(d, row.channelType)
      return d
    })

    const keptKeys = new Set(next.map(d => d.key))
    const removed = devices.filter(d => !keptKeys.has(d.key))
    const lostTags = removed.reduce((n, d) => n + d.tags.length, 0)
    onChange(next)
    if (removed.length > 0) onNotice(`디바이스 ${removed.length}대${lostTags ? `(태그 ${lostTags}개)` : ''}를 지웠습니다. Ctrl+Z 로 되돌릴 수 있습니다.`)
  }

  // 다른 사람이 있는 칸 (시트에서 고르고 있는 칸) · 디바이스별 작업 중인 사람 (어느 화면이든)
  const remote = useMemo(() => cellMarks(viewers, 'sheet', v => v.cursor?.device), [viewers])
  const others = useCallback((key: string) => viewers.filter(v => v.cursor?.device === key), [viewers])

  const leaveRow = useLeaveBlankRow(gridRef, displayRef, r => isBlank(r) && byKey.has(r.key) && isEmptyDevice(byKey.get(r.key)),
    key => onChange(devices.filter(d => d.key !== key)))

  const columns = useMemo<Column<DeviceRow>[]>(() => {
    // 점검에서 빠진 칸은 붉게, 다른 사람이 있는 칸은 그 사람 색 테두리
    const cls = (field: DeviceGridField, ...issueFields: ('name' | 'code' | 'channelType' | 'endpoint')[]) => ({ rowData }: { rowData: DeviceRow }) =>
      [showIssues && issueFields.some(f => issues.device[rowData.key]?.[f]) ? 'dsg-cell-invalid' : '',
        markClass(remote, rowData.key, field),
        field === 'code' ? 'font-mono' : ''].filter(Boolean).join(' ') || undefined
    const tagIssues = (d: DeviceSpecDevice) => (showIssues ? d.tags.filter(t => issues.tag[t.key]).length : 0)
    return [
      { ...keyColumn<DeviceRow, 'category'>('category', text()), title: '설비구분', basis: 110, grow: 1, minWidth: 80, cellClassName: cls('category') },
      { ...keyColumn<DeviceRow, 'maker'>('maker', text()), title: '제조사', basis: 100, grow: 1, minWidth: 70, cellClassName: cls('maker') },
      { ...keyColumn<DeviceRow, 'model'>('model', text()), title: '모델', basis: 120, grow: 1, minWidth: 80, cellClassName: cls('model') },
      { ...keyColumn<DeviceRow, 'name'>('name', text()), title: '디바이스 이름', basis: 160, grow: 2, minWidth: 110, cellClassName: cls('name', 'name') },
      {
        ...keyColumn<DeviceRow, 'channelType'>('channelType', channelColumn), title: '프로토콜', basis: 200, grow: 1, minWidth: 140,
        cellClassName: cls('channelType', 'channelType', 'endpoint'), // 접속은 [설정] 에서 — 빠졌으면 여기 표시
      },
      {
        ...keyColumn<DeviceRow, 'code'>('code', text()),
        title: <span title="Edge 전송 때 입력">디바이스 코드</span>,
        basis: 120, grow: 0, minWidth: 90, cellClassName: cls('code', 'code'),
      },
      {
        id: 'actions', title: '', basis: 200, grow: 0, minWidth: 130, disabled: true,
        component: ActionCell as Column<DeviceRow, ActionData>['component'],
        columnData: { devices: byKey, tagIssues, onEditTags, onOpenPanel, others },
        copyValue: () => null, pasteValue: ({ rowData }) => rowData, deleteValue: ({ rowData }) => rowData,
      } as Column<DeviceRow, ActionData>,
    ] as Column<DeviceRow>[]
  }, [issues, showIssues, byKey, onEditTags, onOpenPanel, others, remote])

  return (
    // isolate — 표 안의 z-index(채우기 핸들·선택 테두리)가 위의 sticky 툴바(z-20)와 겨루지 않게 표를 하나의 층으로
    <div ref={wrapRef} className="spec-grid device-grid relative isolate bg-surface rounded-xl border border-gray-200 overflow-hidden">
      <DynamicDataSheetGrid<DeviceRow>
        ref={gridRef}
        onActiveCellChange={({ cell }) => {
          fill.onActiveCell(cell, !!gridRef.current?.selection)
          setActiveCell(cell ? { col: cell.col, row: cell.row } : null)
          const key = cell ? display[cell.row]?.key : undefined
          leaveRow(cell)
          locateCell(key && !key.startsWith(PAD) ? key : null, cell ? COLUMN_FIELDS[cell.col] ?? null : null)
        }}
        onSelectionChange={({ selection }) => fill.onSelection(selection)}
        onScroll={fill.place}
        value={display}
        onChange={handleChange}
        columns={columns}
        rowKey="key"
        createRow={() => blankRow(newKey())}
        duplicateRow={({ rowData }) => ({ ...rowData, key: newKey(), from: rowData.from ?? rowData.key, code: '', name: rowData.name ? `${rowData.name} (복사)` : '' })}
        rowClassName={({ rowData }) => (rowData.key.startsWith(PAD) ? 'device-row-pad' : undefined)}
        rowHeight={ROW_HEIGHT}
        headerRowHeight={ROW_HEIGHT}
        // 표 안에서 따로 스크롤하지 않고 페이지가 스크롤된다 (디바이스 수십 대 규모)
        height={ROW_HEIGHT * (display.length + 1) + 2}
        disableSmartDelete
        disableExpandSelection
        addRowsComponent={false}
        contextMenuComponent={ContextMenu}
      />
      {fill.element}
    </div>
  )
}
